import { httpClient } from "api/httpClient";
import { useCallback, useEffect, useRef, useState } from "react";

export const useBaseSummary = ({
	endpoint,
	buildFilter,
	debounceMs = 0,
	enabled = true,
	autoFetch = true,
}) => {
	const [summaryTotals, setSummaryTotals] = useState(null);
	const [loading, setLoading] = useState(false);
	const debounceRef = useRef(null);
	const abortRef = useRef(null);

	const fetchSummary = useCallback(() => {
		if (!enabled) {
			setSummaryTotals(null);
			return;
		}

		const filter = buildFilter?.();
		if (!filter) {
			setSummaryTotals(null);
			return;
		}

		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}
		if (abortRef.current) {
			abortRef.current.abort();
		}

		const doFetch = () => {
			abortRef.current = new AbortController();
			setLoading(true);
			const query = encodeURIComponent(JSON.stringify({ ...filter, no_range: true }));

			httpClient(`${endpoint}?filter=${query}`, {
				method: "GET",
				signal: abortRef.current.signal,
			})
				.then(({ json }) => {
					setSummaryTotals(json);
				})
				.catch((err) => {
					if (err?.name !== "AbortError") {
						setSummaryTotals(null);
					}
				})
				.finally(() => {
					setLoading(false);
				});
		};

		if (debounceMs > 0) {
			debounceRef.current = setTimeout(doFetch, debounceMs);
			return;
		}
		doFetch();
	}, [endpoint, buildFilter, debounceMs, enabled]);

	useEffect(() => {
		if (!autoFetch) {
			return () => {
				if (debounceRef.current) {
					clearTimeout(debounceRef.current);
				}
				if (abortRef.current) {
					abortRef.current.abort();
				}
			};
		}

		fetchSummary();
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			if (abortRef.current) {
				abortRef.current.abort();
			}
		};
	}, [fetchSummary, autoFetch]);

	return {
		summaryTotals,
		loading,
		refetch: fetchSummary,
	};
};
